import { useState } from 'react';
import { formatMinorUsd, type AdminInvoice, type InvoiceShareLinkResponse } from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import { createInvoiceShareLink, sendInvoice } from '../api/invoices.js';

function idempotencyKey(storageKey: string): string {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }
  const key = crypto.randomUUID();
  sessionStorage.setItem(storageKey, key);
  return key;
}

function storedShareUrl(invoiceId: string): string | undefined {
  return sessionStorage.getItem(`invoice-share-url:${invoiceId}`) ?? undefined;
}

function rememberShare(invoiceId: string, result: InvoiceShareLinkResponse): string | undefined {
  if (result.url) {
    sessionStorage.setItem(`invoice-share-url:${invoiceId}`, result.url);
    if (result.whatsappUrl) {
      sessionStorage.setItem(`invoice-share-wa:${invoiceId}`, result.whatsappUrl);
    }
    return result.url;
  }
  return storedShareUrl(invoiceId);
}

export function InvoiceSharePanel({
  invoice,
  accessToken,
  onInvoiceChange,
  onError,
}: {
  invoice: AdminInvoice;
  accessToken: string;
  onInvoiceChange: (invoice: AdminInvoice) => void;
  onError: (message: string | undefined) => void;
}) {
  const closed = invoice.status === 'VOID';
  const [toEmail, setToEmail] = useState(invoice.customer.email ?? '');
  const [shareUrl, setShareUrl] = useState(storedShareUrl(invoice.invoiceId));
  const [whatsappUrl, setWhatsappUrl] = useState(
    sessionStorage.getItem(`invoice-share-wa:${invoice.invoiceId}`) ?? undefined,
  );
  const [busy, setBusy] = useState<string | undefined>();
  const [copied, setCopied] = useState(false);

  async function onShare(): Promise<void> {
    onError(undefined);
    setBusy('share');
    try {
      const result = await createInvoiceShareLink(
        accessToken,
        invoice.invoiceId,
        idempotencyKey(`invoice-share:${invoice.invoiceId}`),
      );
      const url = rememberShare(invoice.invoiceId, result);
      setShareUrl(url);
      setWhatsappUrl(
        result.whatsappUrl ?? sessionStorage.getItem(`invoice-share-wa:${invoice.invoiceId}`) ?? undefined,
      );
      if (result.replayed && !url) {
        onError('A share link was already created in this session. Copy the saved link or create a new one later.');
      }
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The share link could not be created.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onSend(): Promise<void> {
    onError(undefined);
    setBusy('send');
    try {
      const result = await sendInvoice(
        accessToken,
        invoice.invoiceId,
        { toEmail: toEmail.trim() || undefined },
        idempotencyKey(`invoice-send:${invoice.invoiceId}`),
      );
      onInvoiceChange(result.invoice);
      const url = rememberShare(invoice.invoiceId, result);
      setShareUrl(url);
      setWhatsappUrl(
        result.whatsappUrl ?? sessionStorage.getItem(`invoice-share-wa:${invoice.invoiceId}`) ?? undefined,
      );
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'The invoice could not be emailed.');
    } finally {
      setBusy(undefined);
    }
  }

  async function onCopy(): Promise<void> {
    if (!shareUrl) {
      return;
    }
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }

  if (closed) {
    return null;
  }

  return (
    <section className="card mt-6">
      <h2 className="heading-lg text-crimson">Send and share</h2>
      <p className="mt-2 text-sm text-charcoal/80">
        Email a secure link or copy it for WhatsApp. The link expires after 30 days. FOIS never stores
        the raw token on the invoice.
      </p>
      {invoice.lastSentAt ? (
        <p className="mt-2 text-sm text-charcoal">
          Last sent {new Date(invoice.lastSentAt).toLocaleString()}
        </p>
      ) : null}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          className="field-input flex-1"
          type="email"
          placeholder="Customer email"
          value={toEmail}
          onChange={(event) => setToEmail(event.target.value)}
          aria-label="Recipient email"
        />
        <button
          className="btn-primary"
          type="button"
          disabled={Boolean(busy)}
          onClick={() => void onSend()}
        >
          {busy === 'send' ? 'Sending…' : 'Send invoice'}
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          className="btn-secondary"
          type="button"
          disabled={Boolean(busy)}
          onClick={() => void onShare()}
        >
          {busy === 'share' ? 'Creating…' : 'Create share link'}
        </button>
        {shareUrl ? (
          <button className="btn-secondary" type="button" onClick={() => void onCopy()}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        ) : null}
        {whatsappUrl ? (
          <a className="btn-secondary" href={whatsappUrl} target="_blank" rel="noreferrer">
            WhatsApp
          </a>
        ) : null}
      </div>
      {shareUrl ? (
        <p className="mt-3 break-all text-sm text-charcoal/80">{shareUrl}</p>
      ) : null}
      <p className="mt-3 text-sm text-charcoal">
        Balance due {formatMinorUsd(invoice.balanceDueMinor)}
      </p>
    </section>
  );
}
